import { Container } from "aurelia-dependency-injection";
import { computed, defineComponent } from "vue";
import { mapState } from "pinia";
import moment from "moment";

import { useConvertToPendingPal } from "../../../store/useConvertToPendingPal";
import { PatientService } from "../../../../../../services/patient.service";
import {
    IGetDocumentTypeByName,
    IGetDocumentTypeLean
} from "../../../../../../resources-vue/vue-interfaces/i-document-type";
import { TaskService } from "../../../../../../services/task.service";
import { CurrentUserService } from "../../../../../../services/current-user-service";
import { IAvailableTasksResponse } from "../../../../../../resources-vue/vue-interfaces/i-patient-task-hos-pal";
import { ICreateTask } from "../../../../../../resources-vue/vue-interfaces/i-task";
import { ToastrService } from "../../../../../../services/toastr.service";
import { IPatientDialogOptions } from "../../../../../../resources-vue/vue-interfaces/i-get-patient";
import { HistoryType } from "../../../../../../enums/HistoryTypeEnum";
import { PediatricHistory } from "../../../../../../models/pal-health-history/pediatric-history";
import { DocumentTypeCategory, WeightScale, HeightScale } from "../../../../../../enums/enums";
import { ReferralService } from "../../../../../../services/referral-service";

import FormSection from "../../../../../../resources-vue/vue-custom-elements/FormSection/FormSection.vue";
import PastDiagnosisHistory from "../PastDiagnosisHistory/PastDiagnosisHistory.vue";
import ProblemsTable from "../ProblemsTable/ProblemsTable.vue";
import CommentInput from "../../../../../../resources-vue/vue-custom-elements/CommentInput/CommentInput.vue";
import HealthHistorySection from "../HealthHistorySection/HealthHistorySection.vue";
import PediatricHistoryForm from "../PediatricHistoryForm/PediatricHistoryForm.vue";
import Upload from "../../../../../../resources-vue/vue-dialogs/UploadPatientDocuments/Upload.vue";
import PatientDocuments from "../../../../CreateReferralHosPal/components/PatientDocuments/PatientDocuments.vue";
import FamilyHealthHistory from "../FamilyHealthHistory/FamilyHealthHistory.vue";
import HospitalizationsTable from "../HospitalizationTable/HospitalizationsTable.vue";

export default defineComponent({
    components: {
        FormSection,
        PastDiagnosisHistory,
        ProblemsTable,
        CommentInput,
        PatientDocuments,
        Upload,
        HealthHistorySection,
        PediatricHistoryForm,
        FamilyHealthHistory,
        HospitalizationsTable
    },

    data(vm) {
        return {
            _referralService: null,
            _toastrService: null,
            _currentUsersService: null,
            _taskService: null,
            _patientsService: null,
            isLoadingComment: false,
            dialogRef: null,
            openDocuments: false as boolean,
            historyType: HistoryType,
            historyTypeEnum: Array.from(HistoryType.enumWithDescriptions.values()),
            isLoading: false as boolean,
            birthWeightLarge: 0 as number,
            birthWeightSmall: 0 as number,
            weightScaleEnumMap: WeightScale,
            lengthScaleEnumMap: HeightScale,
            isError: false as boolean
        };
    },

    provide() {
        return {
            dialogRef: computed(() => this.dialogRef)
        };
    },

    async created() {
        this._patientsService = Container.instance.get(PatientService);
        this._toastrService = Container.instance.get(ToastrService);
        this._referralService = Container.instance.get(ReferralService);
        this._currentUsersService = Container.instance.get(CurrentUserService);
        this._taskService = Container.instance.get(TaskService);
    },

    async mounted() {
        this.isLoading = true;
        // sorting the enum as product requested smoking status fields to be below social history fields
        this.historyTypeEnum = this.historyTypeEnum.sort((a, b) => {
            if (a.name < b.name) {
                return -1;
            }
            if (a.name > b.name) {
                return 1;
            }
            return 0;
        });
        await this.loadHistories();
        this.isLoading = false;
    },
    computed: {
        ...mapState(useConvertToPendingPal, [
            "application",
            "patientDetails",
            "patientId",
            "patientOptions",
            "accountId"
        ]),
        isCircumferenceImperial() {
            if (!this.patientDetails.healthHistory.pediatricHistory.headCircumferenceScale) {
                this.patientDetails.healthHistory.pediatricHistory.headCircumferenceScale = Number(
                    this.lengthScaleEnumMap.enum.In
                );
            }
            return (
                this.lengthScaleEnumMap?.enum.In ===
                this.patientDetails.healthHistory.pediatricHistory.headCircumferenceScale
            );
        },
        isWeightImperial() {
            if (!this.patientDetails.healthHistory.pediatricHistory.birthWeightScale) {
                this.patientDetails.healthHistory.pediatricHistory.birthWeightScale = Number(
                    this.weightScaleEnumMap.enum.Lbs
                );
            }
            this.divideWeights();
            return (
                this.weightScaleEnumMap?.enum.Lbs ===
                this.patientDetails.healthHistory.pediatricHistory.birthWeightScale
            );
        },
        isLengthImperial() {
            if (!this.patientDetails.healthHistory.pediatricHistory.lengthScale) {
                this.patientDetails.healthHistory.pediatricHistory.lengthScale = Number(
                    this.lengthScaleEnumMap.enum.In
                );
            }
            return this.lengthScaleEnumMap?.enum.In === this.patientDetails.healthHistory.pediatricHistory.lengthScale;
        }
    },
    watch: {
        birthWeightLarge() {
            this.divideWeights();
        },
        birthWeightSmall() {
            this.divideWeights();
        }
    },

    methods: {
        async loadHistories() {
            try {
                if (!this.patientDetails?.healthHistory?.pediatricHistory) {
                    this.patientDetails.healthHistory.pediatricHistory = new PediatricHistory();
                }
                this.checkHasPediatricHistory();
                if (this.patientDetails?.healthHistory?.showPediatricHistory) {
                    if (
                        this.patientDetails?.healthHistory?.pediatricHistory?.birthWeightScale ===
                        Number(this.weightScaleEnumMap.enum.Kg)
                    ) {
                        this.birthWeightLarge = this.patientDetails.healthHistory.pediatricHistory.birthWeight / 1000;
                    }
                    this.birthWeightSmall = this.patientDetails.healthHistory.pediatricHistory.birthWeight;
                    this.divideWeights();
                }
                if (!this.patientDetails?.healthHistory?.patientClinicalHistories?.length) {
                    this.patientDetails.healthHistory.patientClinicalHistories = [];
                    this.historyTypeEnum.forEach((type) => {
                        this.patientDetails.healthHistory.patientClinicalHistories.push({
                            type: Number(type.value),
                            description: ""
                        });
                    });
                } else if (
                    this.patientDetails.healthHistory.patientClinicalHistories.length < this.historyTypeEnum.length
                ) {
                    this.historyTypeEnum.forEach((type) => {
                        let hasType = this.patientDetails.healthHistory.patientClinicalHistories.some(
                            (clinicalHistory: any) => clinicalHistory.type === type.value
                        );
                        if (!hasType) {
                            this.patientDetails.healthHistory.patientClinicalHistories.push({
                                type: Number(type.value),
                                description: ""
                            });
                        }
                    });
                }
                if (this.patientDetails?.healthHistory?.pastMedicalHistories?.length >= 0) {
                    this.patientDetails?.healthHistory?.pastMedicalHistories?.forEach(
                        (history: any) => (history.order = history.resolvedOrder)
                    );
                }
                if (!this.patientDetails?.healthHistory?.pastMedicalHistoryComment) {
                    this.patientDetails.healthHistory.pastMedicalHistoryComment = "";
                }
                this.isLoading = false;
            } catch (err) {
                this.isLoading = false;
                console.error(err);
                this.isError = true;
                this._toastrService.error({
                    title: "Error",
                    message: "There was a problem while loading patient's clinical history."
                });
            }
        },

        deleteDocument(patientId: string, documentId: string) {
            return this._patientsService.deleteDocument(patientId, documentId);
        },

        async getLabsDocuments(id: string) {
            const queryParams = {
                appliation: this.application,
                accountId: this.accountId
            };
            return await this._patientsService.getPatientDocumentsByType(this.patientId, id, queryParams);
        },

        async getScansDocuments(id: string) {
            const queryParams = {
                appliation: this.application,
                accountId: this.accountId
            };
            return await this._patientsService.getPatientDocumentsByType(this.patientId, id, queryParams);
        },

        async commentDocument() {
            try {
                this.isLoadingComment = true;
                let user = await this._currentUsersService.getUser();
                let userId = user.id;
                let startDate = [moment().format("MM/DD/YYYY")];
                let agencyTasksList: IAvailableTasksResponse = await this._taskService?.getAvailableTasks(
                    this.patientId,
                    {
                        startDates: startDate,
                        userId: userId
                    }
                );
                let careCoordinationNoteType = agencyTasksList.Data.find(
                    (task) => task.name === "Care Coordination Note"
                );
                let payers = await this._patientsService.getCurrentPatientPayors(this.patientId, null);
                let careCoordinationNote: ICreateTask = {
                    taskId: careCoordinationNoteType.id,
                    patientId: this.patientId,
                    userId: user.id,
                    startDates: startDate,
                    patientPayerId: payers.items[0].id
                };
                let taskResponse = await this._taskService.createPalliativeTask(careCoordinationNote);
                let noteId = taskResponse.ids[0];
                if (!!noteId) {
                    let relativeRoute = this.$router.resolve({ name: "note", params: { id: noteId } }).href;
                    window.open(relativeRoute);
                }
            } catch (e) {
                console.error(e);
                this._toastrService.error({
                    title: `Error - Document`,
                    message: `There was a problem while commenting on the document. Please try again.`
                });
            } finally {
                this.isLoadingComment = false;
            }
        },
        async manageDocuments() {
            let uploadOptions: IPatientDialogOptions = {
                title: "Documents",
                patientId: this.patientId,
                isEditPatient: true,
                providerId: this.patientOptions.data.providerId,
                application: this.application,
                accountId: this.accountId
            };
            this.dialogRef = this.$dialog.open(Upload, {
                props: {
                    modal: true,
                    closeOnEscape: false,
                    showHeader: false,
                    style: {
                        width: "60vw"
                    }
                },
                data: uploadOptions,
                onClose: async (options) => {
                    if (options.data == "cancel") {
                        this.openDocuments = false;
                    }
                }
            });
        },
        divideWeights() {
            if (!this.patientDetails.healthHistory?.pediatricHistory) {
                return;
            }
            let maxVal: number =
                this.patientDetails.healthHistory.pediatricHistory.birthWeightScale ===
                Number(this.weightScaleEnumMap.enum.Lbs)
                    ? 16
                    : 1000;
            if (
                this.patientDetails.healthHistory.pediatricHistory.birthWeightScale ===
                Number(this.weightScaleEnumMap.enum.Kg)
            ) {
                this.birthWeightSmall = 0;
            }
            if (this.birthWeightSmall >= maxVal) {
                let oldSmallWeight = this.birthWeightSmall;
                this.birthWeightSmall %= maxVal;
                this.birthWeightLarge = (oldSmallWeight - this.birthWeightSmall) / maxVal;
            }
            this.patientDetails.healthHistory.pediatricHistory.birthWeight =
                Number(this.birthWeightSmall) + Number(this.birthWeightLarge) * maxVal;
        },
        handleShowPediatricHistory() {
            if (this.patientDetails.healthHistory.showPediatricHistory === false) {
                this.patientDetails.healthHistory.pediatricHistory.tbTest = null;
                this.patientDetails.healthHistory.pediatricHistory.gestationalAgeAtBirth = null;
                this.patientDetails.healthHistory.pediatricHistory.newBornHistory = false;
                this.patientDetails.healthHistory.pediatricHistory.leadScreening = null;
                this.patientDetails.healthHistory.pediatricHistory.otherPediatricScreening = "";
                this.patientDetails.healthHistory.pediatricHistory.lengthScale = 1;
                this.patientDetails.healthHistory.pediatricHistory.lengthAtBirth = null;
                this.patientDetails.healthHistory.pediatricHistory.fontanels = [];
                this.patientDetails.healthHistory.pediatricHistory.umbilicus = [];
                this.patientDetails.healthHistory.pediatricHistory.newBornScreeningResult = "";
                this.patientDetails.healthHistory.pediatricHistory.birthWeightScale = 1;
                this.patientDetails.healthHistory.pediatricHistory.headCircumferenceScale = 1;
                this.patientDetails.healthHistory.pediatricHistory.headCircumference = null;
                this.patientDetails.healthHistory.pediatricHistory.birthWeight = null;
                this.patientDetails.healthHistory.pediatricHistory.otherPediatricScreening = "";
            }
        },
        checkHasPediatricHistory() {
            this.patientDetails.healthHistory.showPediatricHistory = Object.values(
                this.patientDetails.healthHistory.pediatricHistory
            ).some((option) => {
                if (!option) {
                    return false;
                }
                if (Array.isArray(option)) {
                    return option.length > 0;
                }
                return true;
            });
        },
        resultUpdatedAppended(value: string, index: number) {
            this.patientDetails.healthHistory.patientClinicalHistories[index].description += value;
        },
        resultUpdated(value: string, index: number) {
            this.patientDetails.healthHistory.patientClinicalHistories[index].description = value;
        },
        medicalResultUpdatedAppended(value: string) {
            this.patientDetails.healthHistory.pastMedicalHistoryComment += value;
        },
        medicalResultUpdated(value: string) {
            this.patientDetails.healthHistory.pastMedicalHistoryComment = value;
        }
    }
});

